---
title: "Automat wykrywa 30% błędów WCAG. Resztę musi znaleźć człowiek."
slug: ai-w-audycie-wcag-co-widzi-automat
date: 2026-03-17
description: "Narzędzia automatyczne wykrywają ok. 30% błędów dostępności. Kontekst, logika i zachowanie strony wymagają oceny eksperta. Dowiedz się, gdzie kończy się automatyczne badanie WCAG."
tags: [audyt-WCAG, testy-manualne, metodologia, dostępność-cyfrowa]
category: "Metodologia"
readingTime: 5
---

4 na 5 specjalistów od dostępności cyfrowej szacuje, że automatyczne narzędzia wyłapują mniej niż połowę błędów WCAG. Większość podaje 30%.

To dane z [badania 758 praktyków (WebAIM)](https://webaim.org/projects/practitionersurvey3/) — ludzi, którzy robią badania WCAG na co dzień. [Analiza Ministerstwa Cyfryzacji z 2025 roku](https://www.gov.pl/web/dostepnosc-cyfrowa/raport--analiza-mozliwosci-wykorzystania-sztucznej-inteligencji-w-obszarze-badania-dostepnosci-cyfrowej) idzie dalej: rekomenduje podejście hybrydowe — narzędzie automatyczne plus ekspert.

To nie jest argument przeciwko automatyzacji. To argument za tym, żeby wiedzieć, gdzie w badaniu dostępności kończy się maszyna i zaczyna człowiek.

---

## Co narzędzie automatyczne wykryje w kilka sekund

Walidator w ciągu sekund przejdzie przez każdy element strony. Znajdzie naruszenia, których ręczne sprawdzenie zajęłoby godziny:

- **Obrazy bez opisów** — obraz bez tekstu alternatywnego jest niewidoczny dla czytnika ekranu
- **Przyciski i linki bez nazwy** — czytnik mówi „przycisk", ale nie mówi jaki
- **Formularze bez etykiet** — pole bez opisu, którego przeznaczenie trzeba zgadywać
- **Kontrast kolorów poniżej normy** — tekst nieczytelny dla osób z niedowidzeniem
- **Błędy struktury nagłówków** — np. przeskok z pierwszego na czwarty poziom

{/* TODO: link-internal — post o axe-core i co konkretnie skanuje */}

Narzędzia takie jak axe-core to dobry punkt wyjścia. Bez nich badanie WCAG zaczyna się od zera.

---

## Czego żadne narzędzie automatyczne nie wykryje w testach dostępności

**Czy użytkownik klawiatury przejdzie przez formularz?**

Wciśnij klawisz Tab i obserwuj. Fokus powinien przejść kolejno przez pola — imię, nazwisko, email. Jeśli zamiast tego wpadnie do stopki strony, masz problem, którego żaden walidator nie zarejestruje.

Narzędzie automatyczne sprawdzi, czy elementy w ogóle reagują na klawiaturę — ale nie sprawdzi kolejności. Nie wykryje, że po wpisaniu imienia fokus trafia do stopki zamiast w pole nazwiska. Nie sprawdzi, czy okno dialogowe da się zamknąć klawiszem Escape. Nie zauważy, że użytkownik gubi się w połowie formularza i nie ma jak wrócić. To wykryją testy manualne — nie skrypt.

**Czy opis obrazu mówi coś sensownego?**

Obraz z atrybutem `alt="logo"` technicznie spełnia wymaganie — walidator nie zgłosi błędu. Ale jeśli ten obraz jest jednocześnie linkiem do strony głównej, "logo" to za mało. Powinno być: "Urząd Gminy Przykład — strona główna". Albo odwrotnie: obraz dekoracyjny w tle powinien mieć pusty opis, żeby czytnik go pominął. Żadna z tych decyzji nie należy do automatycznego narzędzia.

**Napisy w filmie — plik jest, ale treść nie działa**

Walidator sprawdzi, czy film ma dołączony plik napisów. Nie sprawdzi, czy napisy są zsynchronizowane, czy opisują dźwięki pozawerbalne — syrenę, brawa, ciszę — ani czy są czytelne na tle zmieniającego się obrazu. Plik jest? Narzędzie automatyczne jest zadowolone. Użytkownik — niekoniecznie.

**Komunikat błędu: "Pole wymagane" — ale które?**

Walidator nie oceni, czy komunikat błędu wskazuje konkretne pole formularza. Nie sprawdzi, czy strona daje wystarczający czas przed wygaśnięciem sesji. Nie powie, czy wieloetapowy formularz jasno pokazuje aktualny krok. To wszystko wymaga oceny człowieka — osoby, która przechodzi przez stronę jak jej rzeczywisty użytkownik.

**[9 na 10 użytkowników czytnika ekranu](https://webaim.org/projects/screenreadersurvey10/) korzysta z niego na telefonie**

Walidator testuje kod. Nie testuje, jak strona zachowuje się na iPhonie z VoiceOver ani na Androidzie z TalkBack. Gesty dotykowe, powiększanie, orientacja ekranu — to testy, które musi wykonać człowiek.

---

## Jak ta różnica wpływa na badanie dostępności WCAG w praktyce

Dwa badania tej samej strony dadzą różne wyniki — i to nie znaczy, że jedno jest błędne. Zależy od zakresu.

Badanie oparte wyłącznie na narzędziu automatycznym zwróci błędy wykrywalne w kodzie. Szybkie. Tanie. Za mało, żeby ocenić zgodność z ustawą. Za mało, żeby napisać wiarygodną deklarację dostępności.

{/* TODO: link-internal — post o deklaracji dostępności i co musi zawierać */}

Badanie z testami manualnymi — klawiatura, czytnik ekranu, formularz od początku do końca — pokaże kontekst, logikę i zachowanie strony. Znajdzie resztę błędów, tę której narzędzie automatyczne nie widzi. To dwa różne produkty — i warto wiedzieć, który zamawiasz.

---

## Jedno pytanie przed zamówieniem audytu WCAG

Zapytaj wprost: **czy w zakres badania wchodzą testy manualne z klawiaturą i czytnikiem ekranu?**

Odpowiedź wymijająca albo brak odpowiedzi — to już informacja. Raport oparty wyłącznie na narzędziu automatycznym powstaje szybko. Ale szybko wyprodukowany raport i rzetelny audyt dostępności to dwie różne usługi.

---

{/* CTA — wydzielony element wizualny */}
{/* Sprawdź jak Twoja strona wypada w audycie dostępności. Wyślij adres na kontakt@bastunit.pl — wrócimy z wstępną diagnozą. */}
